import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CheckCircle2, AlertCircle, ArrowLeft, History } from "lucide-react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";

export default function AdminCheckin() {
  const [scanning, setScanning] = useState(false);
  const [last, setLast] = useState(null);  // { ok, member, visit } | { error }
  const [recent, setRecent] = useState([]);
  const [manualToken, setManualToken] = useState("");
  const qrRef = useRef(null);
  const qrInstance = useRef(null);
  const lastScanRef = useRef(0);

  const loadRecent = async () => {
    try {
      const r = await api.get("/admin/checkins");
      setRecent(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadRecent(); }, []);

  const checkin = async (token) => {
    try {
      const r = await api.post("/admin/checkin", { token });
      setLast({ ok: true, ...r.data });
      toast.success(`${r.data.member.full_name} · checked in`);
      loadRecent();
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not verify";
      setLast({ ok: false, error: msg });
      toast.error(msg);
      return false;
    }
  };

  const startScan = async () => {
    if (scanning) return;
    setScanning(true);
    setLast(null);
    try {
      if (!qrInstance.current) qrInstance.current = new Html5Qrcode("twr-qr-reader");
      await qrInstance.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decoded) => {
          const now = Date.now();
          if (now - lastScanRef.current < 3000) return;
          lastScanRef.current = now;
          await checkin(decoded);
        },
        () => {}
      );
    } catch (e) {
      setScanning(false);
      toast.error("Could not access camera. Use manual entry below.");
    }
  };

  const stopScan = async () => {
    try {
      if (qrInstance.current && scanning) {
        await qrInstance.current.stop();
        await qrInstance.current.clear();
      }
    } catch (e) { /* ignore */ }
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []); // eslint-disable-line

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualToken.trim()) return;
    const ok = await checkin(manualToken.trim());
    if (ok) setManualToken("");
  };

  return (
    <section className="min-h-screen bg-[#0A0A0A]" data-testid="admin-checkin-page">
      <header className="border-b border-[var(--twr-line)] px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/"><Logo variant="horizontal" /></Link>
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/65 hover:text-[var(--twr-bronze)] transition-colors">
          <ArrowLeft size={13} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1400px] mx-auto p-6 sm:p-10 grid lg:grid-cols-12 gap-10">
        <section className="lg:col-span-7">
          <p className="eyebrow">Door · Check-In</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Scan a member's card.</h1>
          <p className="text-[var(--twr-warm-white)]/65 mt-3">Point the camera at the QR code on the member's phone. Codes rotate hourly.</p>

          <div className="mt-8 surface p-6">
            <div id="twr-qr-reader" ref={qrRef} className="overflow-hidden bg-black" style={{ minHeight: 320 }} data-testid="qr-reader-container" />
            <div className="mt-5 flex gap-3">
              {!scanning ? (
                <button onClick={startScan} data-testid="start-scan-btn" className="btn-bronze">
                  <Camera size={14} /> Start Camera
                </button>
              ) : (
                <button onClick={stopScan} data-testid="stop-scan-btn" className="btn-ghost">Stop Camera</button>
              )}
            </div>

            <div className="hairline my-6" />

            <form onSubmit={submitManual} className="space-y-3">
              <p className="eyebrow">Manual entry (paste token)</p>
              <textarea data-testid="manual-token-input" value={manualToken} onChange={(e) => setManualToken(e.target.value)} rows={3}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…"
                className="w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-xs font-mono transition-colors" />
              <button data-testid="manual-checkin-btn" className="btn-ghost">Verify Token</button>
            </form>
          </div>

          {/* Result panel */}
          {last && last.ok && (
            <div className="mt-6 surface p-7 border-l-2 border-[var(--twr-bronze)]" data-testid="checkin-result-success">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={22} className="text-[var(--twr-bronze)]" strokeWidth={1.4} />
                <p className="eyebrow">Verified</p>
              </div>
              <h2 className="font-serif text-3xl mt-4">{last.member.full_name}</h2>
              <p className="font-serif text-lg text-[var(--twr-bronze)] mt-1">{last.member.member_no} · {last.member.membership_type}</p>
              <p className="text-[var(--twr-warm-white)]/55 text-sm mt-3">Checked in at {new Date(last.visit.checked_in_at).toLocaleString("en-US")}</p>
            </div>
          )}
          {last && !last.ok && (
            <div className="mt-6 surface p-7 border-l-2 border-red-500/60" data-testid="checkin-result-error">
              <div className="flex items-center gap-3">
                <AlertCircle size={22} className="text-red-400" strokeWidth={1.4} />
                <p className="eyebrow">Not Verified</p>
              </div>
              <p className="text-[var(--twr-warm-white)]/85 mt-4">{last.error}</p>
            </div>
          )}
        </section>

        <aside className="lg:col-span-5">
          <div className="flex items-center justify-between mb-5">
            <p className="eyebrow">Today's Door</p>
            <History size={14} className="text-[var(--twr-bronze)]" />
          </div>
          <div className="space-y-2">
            {recent.length === 0 ? (
              <p className="text-[var(--twr-warm-white)]/55 text-sm">No check-ins yet.</p>
            ) : recent.slice(0, 20).map((v) => (
              <div key={v.id} data-testid={`checkin-row-${v.id}`} className="surface px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-serif text-base">{v.full_name}</p>
                  <p className="text-[var(--twr-warm-white)]/55 text-[11px] tracking-[0.22em] uppercase mt-1">{v.member_no} · {v.membership_type}</p>
                </div>
                <span className="text-[var(--twr-bronze)] text-[12px] font-serif">
                  {new Date(v.checked_in_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
